/**
 * Reload once when a new build takes over the page — and never in a loop.
 *
 * When a new service worker takes control, the page in front of the user is
 * still the old build, so it reloads (main.tsx explains why: three deployed
 * fixes once failed to reach anybody without this). The guard was a flag on
 * the page, which only stops a second reload WITHIN one page load. On 22 Oct
 * 2026 Nick's browser reloaded Today about twice a second, every load ending
 * in another takeover, and nothing carried across loads to stop it.
 *
 * Two rules now, either of which breaks a loop:
 *
 *   - A page that loaded with NO worker controlling it came from the network,
 *     so it already is the newest build. A worker claiming it changes nothing
 *     on screen; it is not a reason to reload.
 *   - Never twice within RELOAD_GAP_MS. A takeover inside that window leaves
 *     the page as it is; the next open picks the build up.
 */
export const RELOAD_GAP_MS = 30_000;
const KEY = 'adhd-me:sw-reloaded-at';

interface Deps {
  sw: Pick<ServiceWorkerContainer, 'controller' | 'addEventListener'>;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload: () => void;
  now?: () => number;
}

export function reloadOnTakeover({ sw, storage, reload, now = Date.now }: Deps): void {
  const startedControlled = sw.controller !== null && sw.controller !== undefined;
  let reloading = false;
  sw.addEventListener('controllerchange', () => {
    if (reloading || !startedControlled) return;
    try {
      const last = Number(storage?.getItem(KEY) ?? 0);
      if (now() - last < RELOAD_GAP_MS) return;
      storage?.setItem(KEY, String(now()));
    } catch {
      /* no storage: the per-page flag is the only guard left */
    }
    reloading = true;
    reload();
  });
}
